/**
 * NotesPanel — typed/tagged note display for a reunification event subject
 * (ADR-121 P3).
 *
 * Reads and writes the ReunificationNote API for both the student and
 * staff panels. Notes are typed (general / special_instruction
 * / medical / leo_interview / counseling per the seeded NoteType
 * catalog), grouped by type on display, and the add-note form lets the
 * operator pick a type before writing.
 *
 * **Visibility model — server is the source of truth.**
 *
 * Restricted notes (NoteType.visibility_tier !== 'public' and the
 * caller lacks the matching visibility_permission) are simply ABSENT
 * from the notes list response — there is no flag distinguishing
 * "redacted" from "doesn't exist". This component does NOT layer a
 * client-side role check on top; it renders whatever the server
 * returns and lets a stray 403 on write surface as a toast. There is no
 * client-side medical-field gate; visibility permissions are real
 * auth_permission rows checked by the server.
 *
 * The type selector in the add-note form is populated from
 * useNoteTypes(), which returns every NoteType the caller can see.
 * If a non-public NoteType is in the catalog but the caller lacks
 * the matching change_<code>_note permission, the server will 403
 * the write — that's surfaced as a toast on submit. We do NOT
 * pre-filter the type selector by permission, because:
 *
 *   1. The frontend has no clean reading of "do I have permission X"
 *      from the typed client today.
 *   2. Server-side enforcement is the only correctness story per
 *      ADR-121 §Decision-2 and the FERPA tier preamble in CLAUDE.md.
 *
 * Operators see a `sensitive` / `restricted` badge next to non-public
 * types so they know what they're looking at before they click submit.
 */

import { useMemo, useState, type ReactElement } from 'react';
import {
  Badge,
  Box,
  Field,
  Flex,
  HStack,
  Spinner,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import {
  Button as ButtonComponent,
  SectionCard,
  SelectInput,
} from '@themes/aegix';
import { toaster } from '../ui/toaster';
import {
  useCreateReunificationNote,
  useDeleteReunificationNote,
  useNoteTypes,
  useReunificationNotes,
  useUpdateReunificationNote,
} from '../../hooks/queries/useReunification';
import type {
  NoteType,
  ReunificationNote,
  ReunificationNoteFilters,
  VisibilityTier,
} from '../../types/ReunificationTypes';

// =============================================================================
// Props
// =============================================================================

/**
 * NotesPanel is subject-agnostic — pass the subject the panel
 * applies to via the `subject` prop. Exactly one of the three
 * fields must be set:
 *
 *   - student: the EventStudent's public_id
 *   - staff:   the EventStaffAssignment's public_id
 *   - guardian: the EventGuardian's public_id
 *
 * The backend's CheckConstraint enforces subject XOR on each note
 * row; we mirror that by accepting only the keyed subject and
 * sending it untouched on create.
 */
export type NotesPanelSubject =
  | { student: string; staff?: undefined; guardian?: undefined }
  | { student?: undefined; staff: string; guardian?: undefined }
  | { student?: undefined; staff?: undefined; guardian: string };

interface NotesPanelProps {
  /** Public ID of the reunification event the notes hang off of. */
  eventId: string;
  /** Which subject this panel applies to (XOR). */
  subject: NotesPanelSubject;
  /**
   * Hide the add-note form when false. Useful when the wider
   * drawer is in a read-only state (e.g., a completed event).
   * Defaults to true. Edit and delete affordances stay visible
   * regardless — the API enforces permission, and hiding the
   * affordances client-side would just confuse operators who
   * have the right but lack the UI.
   */
  canAddNotes?: boolean;
  /**
   * Optional `data-e2e` prefix so callers can scope their
   * test selectors. Defaults to `notes-panel`.
   */
  e2ePrefix?: string;
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Build the filter the notes query is keyed by from the subject
 * prop. Sorting newest-first matches the existing audit-log
 * panel and is what operators expect during an active event
 * (the most recent context is usually what they need).
 */
function filtersFromSubject(
  subject: NotesPanelSubject,
): ReunificationNoteFilters {
  return {
    student: subject.student,
    staff: subject.staff,
    guardian: subject.guardian,
    ordering: '-created_at',
  };
}

/**
 * Group notes by their `note_type` UUID, preserving the order
 * the server sent (newest-first per type because the query
 * itself is `-created_at`).
 */
function groupByType(
  notes: ReunificationNote[],
): Map<string, ReunificationNote[]> {
  const groups = new Map<string, ReunificationNote[]>();
  for (const note of notes) {
    const existing = groups.get(note.note_type);
    if (existing) {
      existing.push(note);
    } else {
      groups.set(note.note_type, [note]);
    }
  }
  return groups;
}

/**
 * Build the display name + sensitivity badge for a NoteType
 * UUID. Falls back to the note's denormalized `note_type_name`
 * field if the NoteType catalog hasn't loaded yet — the server
 * stamps every row with its type's name at write time, so a
 * cold cache still renders correctly (just without the
 * sensitivity badge).
 */
function getTypeMeta(
  typeId: string,
  noteTypes: NoteType[] | undefined,
  fallbackName: string,
  fallbackTier: VisibilityTier,
): { name: string; tier: VisibilityTier } {
  const fromCatalog = noteTypes?.find((t) => t.public_id === typeId);
  if (fromCatalog) {
    return {
      name: fromCatalog.name,
      tier: fromCatalog.visibility_tier,
    };
  }
  return { name: fallbackName, tier: fallbackTier };
}

/**
 * Color palette for the visibility badge.
 */
const VISIBILITY_BADGE: Record<
  VisibilityTier,
  { palette: string; label: string } | null
> = {
  public: null,
  sensitive: { palette: 'orange', label: 'Sensitive' },
  restricted: { palette: 'red', label: 'Restricted' },
};

function VisibilityBadge({ tier }: { tier: VisibilityTier }) {
  const meta = VISIBILITY_BADGE[tier];
  if (!meta) return null;
  // StatusBadge is specifically NOT for taxonomy / classification labels
  // (per its own docstring) — visibility tier is a sensitivity
  // classification, not a workflow status. A raw Chakra <Badge> is the
  // correct primitive here. ADR-118 §Visual-primitives drift.
  return (
    // eslint-disable-next-line aegix/no-inline-status-pill
    <Badge colorPalette={meta.palette} size="sm" data-e2e={`note-tier-${tier}`}>
      {meta.label}
    </Badge>
  );
}

// =============================================================================
// Add-note form (separated for clarity)
// =============================================================================

interface AddNoteFormProps {
  eventId: string;
  subject: NotesPanelSubject;
  noteTypes: NoteType[] | undefined;
  noteTypesLoading: boolean;
  e2ePrefix: string;
}

function AddNoteForm({
  eventId,
  subject,
  noteTypes,
  noteTypesLoading,
  e2ePrefix,
}: AddNoteFormProps) {
  const [typeId, setTypeId] = useState<string>('');
  const [body, setBody] = useState<string>('');

  const createMutation = useCreateReunificationNote(eventId);

  // The select takes display strings for value + label; we map UUID
  // -> name here. `is_active=false` types are still returned by the
  // server for write-side filtering callers, but we exclude them
  // from the picker — operators shouldn't be writing notes of an
  // inactive type.
  const typeOptions = useMemo(() => {
    if (!noteTypes) return [];
    return noteTypes
      .filter((t) => t.is_active !== false)
      .map((t) => ({
        value: t.public_id,
        label:
          t.visibility_tier === 'public'
            ? t.name
            : `${t.name} (${t.visibility_tier})`,
      }));
  }, [noteTypes]);

  const selectedType = useMemo(
    () => noteTypes?.find((t) => t.public_id === typeId),
    [noteTypes, typeId],
  );

  const canSubmit =
    typeId && body.trim().length > 0 && !createMutation.isPending;

  const handleSubmit = () => {
    if (!canSubmit) return;
    createMutation.mutate(
      {
        note_type: typeId,
        body: body.trim(),
        student: subject.student,
        staff: subject.staff,
        guardian: subject.guardian,
      },
      {
        onSuccess: () => {
          toaster.create({ title: 'Note added', type: 'success' });
          setBody('');
          // Keep the type selection — operators often add several notes of
          // the same type in a row during an active event.
        },
        onError: (err) => {
          toaster.create({
            title: 'Failed to add note',
            description:
              err instanceof Error ? err.message : 'Unknown error',
            type: 'error',
          });
        },
      },
    );
  };

  if (noteTypesLoading) {
    return (
      <HStack py={2} justify="center">
        <Spinner size="sm" color="button.primary" />
      </HStack>
    );
  }

  if (!noteTypes || noteTypes.length === 0) {
    return (
      <Text fontSize="sm" color="text.secondary">
        No note types configured. Ask a district admin to add at
        least one note type before recording notes.
      </Text>
    );
  }

  return (
    <VStack align="stretch" gap={3}>
      <Box>
        <SelectInput
          label="Type"
          name={`${e2ePrefix}-add-type`}
          value={typeId}
          onChange={(val: string) => setTypeId(val)}
          option={typeOptions}
          placeholder="Choose a type…"
          e2eTitle={`${e2ePrefix}-add-type`}
        />
        {selectedType && selectedType.visibility_tier !== 'public' ? (
          <Text fontSize="xs" color="text.tertiary" mt={1}>
            {selectedType.visibility_tier === 'restricted'
              ? 'Restricted — visible only to operators with the matching role.'
              : 'Sensitive — visible to operators with the matching role.'}{' '}
            FERPA — Restricted-Student-Record-PII applies.
          </Text>
        ) : null}
      </Box>
      <Field.Root>
        <Field.Label fontSize="sm" color="text.primary">
          Note
        </Field.Label>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Add context the team should see…"
          rows={3}
          disabled={createMutation.isPending}
          data-e2e={`${e2ePrefix}-add-body`}
        />
      </Field.Root>
      <HStack justify="flex-end" gap={2}>
        <ButtonComponent
          visual="primary"
          size="sm"
          onClick={handleSubmit}
          disabled={!canSubmit}
          data-e2e={`${e2ePrefix}-add-submit`}
        >
          {createMutation.isPending ? 'Saving…' : 'Add note'}
        </ButtonComponent>
      </HStack>
    </VStack>
  );
}

// =============================================================================
// Single-note row (separated for inline edit state)
// =============================================================================

interface NoteRowProps {
  note: ReunificationNote;
  eventId: string;
  e2ePrefix: string;
}

function NoteRow({ note, eventId, e2ePrefix }: NoteRowProps) {
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState(note.body);

  const updateMutation = useUpdateReunificationNote(eventId);
  const deleteMutation = useDeleteReunificationNote(eventId);

  const hasChanges = editBody !== note.body;

  const handleSave = () => {
    if (!hasChanges) {
      setEditing(false);
      return;
    }
    updateMutation.mutate(
      { noteId: note.public_id, data: { body: editBody.trim() } },
      {
        onSuccess: () => {
          toaster.create({ title: 'Note updated', type: 'success' });
          setEditing(false);
        },
        onError: (err) => {
          toaster.create({
            title: 'Failed to update note',
            description:
              err instanceof Error ? err.message : 'Unknown error',
            type: 'error',
          });
        },
      },
    );
  };

  const handleDelete = () => {
    deleteMutation.mutate(note.public_id, {
      onSuccess: () => {
        toaster.create({ title: 'Note deleted', type: 'success' });
      },
      onError: (err) => {
        toaster.create({
          title: 'Failed to delete note',
          description:
            err instanceof Error ? err.message : 'Unknown error',
          type: 'error',
        });
      },
    });
  };

  const authorLine = useMemo(() => {
    const ts = note.created_at
      ? new Date(note.created_at).toLocaleString()
      : '';
    const who = note.created_by_name?.trim() || 'Unknown';
    return `${who} • ${ts}`;
  }, [note.created_at, note.created_by_name]);

  return (
    <Box
      borderWidth="1px"
      borderColor="border.subtle"
      borderRadius="md"
      p={3}
      data-e2e={`${e2ePrefix}-row-${note.public_id}`}
    >
      <Flex justify="space-between" align="center" mb={2}>
        <Text fontSize="xs" color="text.tertiary">
          {authorLine}
        </Text>
        {!editing && (
          <HStack gap={1}>
            <ButtonComponent
              visual="secondary"
              size="sm"
              onClick={() => {
                setEditBody(note.body);
                setEditing(true);
              }}
              data-e2e={`${e2ePrefix}-edit-${note.public_id}`}
            >
              Edit
            </ButtonComponent>
            <ButtonComponent
              visual="alert"
              size="sm"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              data-e2e={`${e2ePrefix}-delete-${note.public_id}`}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </ButtonComponent>
          </HStack>
        )}
      </Flex>
      {editing ? (
        <VStack align="stretch" gap={2}>
          <Textarea
            aria-label="Edit note"
            value={editBody}
            onChange={(e) => setEditBody(e.target.value)}
            rows={3}
            disabled={updateMutation.isPending}
            data-e2e={`${e2ePrefix}-edit-body-${note.public_id}`}
          />
          <HStack justify="flex-end" gap={2}>
            <ButtonComponent
              visual="secondary"
              size="sm"
              onClick={() => {
                setEditBody(note.body);
                setEditing(false);
              }}
              disabled={updateMutation.isPending}
              data-e2e={`${e2ePrefix}-edit-cancel-${note.public_id}`}
            >
              Cancel
            </ButtonComponent>
            <ButtonComponent
              visual="primary"
              size="sm"
              onClick={handleSave}
              disabled={updateMutation.isPending || !hasChanges}
              data-e2e={`${e2ePrefix}-edit-save-${note.public_id}`}
            >
              {updateMutation.isPending ? 'Saving…' : 'Save'}
            </ButtonComponent>
          </HStack>
        </VStack>
      ) : (
        <Text
          fontSize="sm"
          color="text.primary"
          whiteSpace="pre-wrap"
          data-e2e={`${e2ePrefix}-body-${note.public_id}`}
        >
          {note.body}
        </Text>
      )}
    </Box>
  );
}

// =============================================================================
// Main panel
// =============================================================================

export function NotesPanel({
  eventId,
  subject,
  canAddNotes = true,
  e2ePrefix = 'notes-panel',
}: NotesPanelProps) {
  const filters = useMemo(() => filtersFromSubject(subject), [subject]);

  const noteTypesQuery = useNoteTypes();
  const notesQuery = useReunificationNotes(eventId, filters);

  const notes = notesQuery.data ?? [];
  const grouped = useMemo(() => groupByType(notes), [notes]);

  const renderGroups = () => {
    if (grouped.size === 0) {
      return (
        <Text fontSize="sm" color="text.secondary">
          No notes yet.
        </Text>
      );
    }
    const groupNodes: ReactElement[] = [];
    grouped.forEach((groupNotes, typeId) => {
      const first = groupNotes[0];
      const meta = getTypeMeta(
        typeId,
        noteTypesQuery.data,
        first.note_type_name,
        first.visibility_tier,
      );
      groupNodes.push(
        <Box key={typeId}>
          <Flex align="center" gap={2} mb={2}>
            <Text
              fontSize="sm"
              fontWeight="semibold"
              color="text.primary"
              data-e2e={`${e2ePrefix}-group-${typeId}`}
            >
              {meta.name}
            </Text>
            <VisibilityBadge tier={meta.tier} />
            <Text fontSize="xs" color="text.tertiary">
              ({groupNotes.length})
            </Text>
          </Flex>
          <VStack align="stretch" gap={2}>
            {groupNotes.map((note) => (
              <NoteRow
                key={note.public_id}
                note={note}
                eventId={eventId}
                e2ePrefix={e2ePrefix}
              />
            ))}
          </VStack>
        </Box>,
      );
    });
    return <VStack align="stretch" gap={4}>{groupNodes}</VStack>;
  };

  return (
    <SectionCard title="Notes">
      <VStack align="stretch" gap={4}>
        {notesQuery.isLoading ? (
          <HStack py={4} justify="center">
            <Spinner size="sm" color="button.primary" />
          </HStack>
        ) : notesQuery.error ? (
          <Text fontSize="sm" color="text.danger" py={2}>
            {notesQuery.error instanceof Error
              ? notesQuery.error.message
              : 'Failed to load notes'}
          </Text>
        ) : (
          renderGroups()
        )}

        {canAddNotes ? (
          <Box borderTopWidth="1px" borderColor="border.subtle" pt={4}>
            <Text fontSize="sm" fontWeight="semibold" mb={2}>
              Add note
            </Text>
            <AddNoteForm
              eventId={eventId}
              subject={subject}
              noteTypes={noteTypesQuery.data}
              noteTypesLoading={noteTypesQuery.isLoading}
              e2ePrefix={e2ePrefix}
            />
          </Box>
        ) : null}
      </VStack>
    </SectionCard>
  );
}
